import { useState, useRef, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  Database,
  ChevronRight,
  ChevronLeft,
  Menu,
  X,
  LogOut,
  Code2,
  AlertTriangle,
  MoreVertical,
  User,
} from "lucide-react";
import models from "../../model-declaration";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import logo from "../../assets/icons/FFlogo.png";
import axios from "axios";
import { url } from "../../config";

const Navbar = ({ onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [user, setData] = useState({});

  const handleConfirmLogout = () => {
    localStorage.removeItem("accessToken");
    setShowLogoutModal(false);
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = () => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      handleLogout();
      return;
    }

    axios
      .post(
        `${url}/ffadmin/auth/fetch-profile`,
        {},
        {
          headers: {
            "x-access-token": token,
          },
        },
      )
      .then((res) => {
        console.log(res);
        setData(res?.data?.data);
      })
      .catch((err) => {
        console.error("API Error:", err);
        const errorMessage = err?.response?.data?.message;

        if (
          errorMessage === "access-token is not valid" ||
          errorMessage === "unable to find access-token"
        ) {
          handleLogout();
        }
      });
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <nav className="sticky top-0 z-40 w-full border-b border-gray-100 bg-white backdrop-blur-md rounded-lg">
        <div className="mx-auto flex items-center justify-between px-4 py-2 sm:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile/Tablet Menu Trigger */}
            <button
              onClick={onMobileMenuToggle}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 lg:hidden"
              aria-label="Open Navigation Menu"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Brand Header */}
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-20 items-center justify-center rounded-full p-2 px-3 text-white shadow-sm shadow-indigo-200">
                <img src={logo} />
              </div>
              <span className="text-lg font-bold tracking-tight text-gray-900 hidden md:block">
                FF Low Code
              </span>
            </div>
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 p-1.5 hover:bg-gray-50 rounded-full transition-all border border-transparent hover:border-gray-100 max-w-[400px]"
            >
              {/* Avatar / Icon Container */}
              <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 font-semibold border border-slate-200 overflow-hidden shrink-0">
                {user?.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user?.name || "User Avatar"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={18} className="text-slate-500" />
                )}
              </div>

              {/* Text Info Container - Added min-w-0 */}
              <div className="hidden sm:block text-left min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 leading-none truncate  max-w-[140px]">
                  {user?.name || "User"}
                </p>
                <p className="text-[11px] text-slate-500 mt-1 truncate max-w-[140px]">
                  {user?.email}
                </p>
              </div>

              <MoreVertical
                size={16}
                className="text-slate-400 ml-1 shrink-0"
              />
            </button>

            {isDropdownOpen && (
              <div className="absolute z-50 right-0 mt-2 w-40 p-1 bg-white rounded-xl shadow-xl border border-gray-100 animate-in fade-in zoom-in duration-150 origin-top-right">
                <button
                  onClick={() => setShowLogoutModal(true)}
                  className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-sm text-red-600 hover:bg-red-50 transition-colors font-medium text-left"
                >
                  <LogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
            onClick={() => setShowLogoutModal(false)}
          />

          {/* Modal Box */}
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Confirm Logout
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Are you sure you want to log out of your account?
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm shadow-red-200 hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
